/** biome-ignore-all lint/correctness/noUnusedImports: reason */
/** biome-ignore-all lint/suspicious/noAssignInExpressions: reason */

import {
  DirectionsRenderer,
  GoogleMap,
  Marker,
  MarkerClusterer,
} from "@react-google-maps/api";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useCompany } from "../../app/context/company";
import { useKegs } from "../../app/context/kegs";
import { debugLog } from "../../app/helpers/debug";
import KegToQuickViewDetails from "../kegs/KegQuickView/KegToQuickViewDetails";
import Places from "./Places";

const MapPage = () => {
  const { companyKegs, partnerKegs } = useKegs();
  const { company } = useCompany();

  const [showInfo, setShowInfo] = useState(false);
  const [coords, setCoords] = useState(null);
  const [directions, setDirections] = useState();
  const [selectedKeg, setSelectedKeg] = useState(null);

  ///////// pinned kegs
  const pinnedKegs = useMemo(() => {
    if (companyKegs && partnerKegs)
      return [
        ...companyKegs,
        ...partnerKegs,
      ]?.filter(keg => keg.lastPinnedLocation);
  }, [
    companyKegs,
    partnerKegs,
  ]);

  /////////// kegs on clients
  const clientKegs = useMemo(() => {
    if (companyKegs && partnerKegs)
      return [
        ...companyKegs,
        ...partnerKegs,
      ]?.filter(keg => keg.clientGps);
  }, [
    companyKegs,
    partnerKegs,
  ]);

  const kegsWithPartner = useMemo(() => {
    if (companyKegs)
      return [
        ...companyKegs,
      ]?.filter(keg => keg.partnerGps);
  }, [
    companyKegs,
  ]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position =>
        setCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      );
    } else {
      debugLog("geolocation is not supported in this browser");
    }
  }, []);

  const mapRef = useRef(null);

  const center = useMemo(
    () => ({
      lat: coords?.latitude,
      lng: coords?.longitude,
    }),
    [
      coords,
    ]
  );

  const options = useMemo(
    () => ({
      clickableIcons: false,
      disableDefaultUI: true,
    }),
    []
  );

  const onLoad = useCallback(map => (mapRef.current = map), []);

  ////////// fetch directions
  const fetchDirections = keg => {
    if (!center) return;

    // eslint-disable-next-line no-undef
    const service = new google.maps.DirectionsService();

    service.route(
      {
        destination: center,
        origin: keg,
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirections(result);
        }
      }
    );
  };

  return (
    <div className="container">
      {company && (
        <>
          <div
            className="controls "
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h1>Search location</h1>

            <Places
              setKegLocation={position => {
                // eslint-disable-next-line no-unused-expressions
                mapRef.current?.panTo(position);
              }}
            />

            <div
              style={{
                height: "80vh",
                margin: "1.5rem 0",
                overflowY: "auto",
              }}
            >
              {selectedKeg && <KegToQuickViewDetails keg={selectedKeg} />}
            </div>

            <div>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <img
                  alt="map icon"
                  src="/assets/images/green_MarkerO.png"
                />
                <span
                  style={{
                    margin: "0 0.5rem",
                  }}
                ></span>
                <span>Pinned kegs</span>
              </div>
            </div>

            <div
              style={{
                alignItems: "center",
                display: "flex",
                marginTop: "0.8rem",
              }}
            >
              <img
                alt="map icon"
                src="/assets/images/blue_MarkerO.png"
              />
              <span
                style={{
                  margin: "0 0.5rem",
                }}
              ></span>
              <span>Kegs with clients</span>
            </div>

            <div
              style={{
                alignItems: "center",
                display: "flex",
                marginTop: "0.8rem",
              }}
            >
              <img
                alt="map icon"
                src="/assets/images/orange_MarkerO.png"
              />
              <span
                style={{
                  margin: "0 0.5rem",
                }}
              ></span>
              <span>Kegs with partners</span>
            </div>

            <div
              style={{
                alignItems: "center",
                display: "flex",
                marginTop: "0.8rem",
              }}
            >
              <img
                alt="map icon"
                src="https://developers.google.com/maps/documentation/javascript/examples/full/images/beachflag.png"
              />

              <span
                style={{
                  margin: "0 0.5rem",
                }}
              ></span>
              <span>Your location</span>
            </div>
          </div>

          <div className="map">
            {pinnedKegs && (
              <GoogleMap
                center={center}
                mapContainerClassName="map-container"
                onLoad={onLoad}
                options={options}
                zoom={10}
              >
                {directions && (
                  <DirectionsRenderer
                    directions={directions}
                    options={{
                      polylineOptions: {
                        strokeColor: "#1976D2",
                        strokeWeight: 5,
                        zIndex: 50,
                      },
                    }}
                  />
                )}

                <Marker
                  icon="https://developers.google.com/maps/documentation/javascript/examples/full/images/beachflag.png"
                  position={center}
                />

                <MarkerClusterer>
                  {clusterer =>
                    pinnedKegs.map(keg => (
                      <Marker
                        clusterer={clusterer}
                        icon="/assets/images/green_MarkerO.png"
                        key={keg?.id}
                        onClick={() => {
                          fetchDirections({
                            lat: +keg?.lastPinnedLocation?.latitude,
                            lng: +keg?.lastPinnedLocation?.longitude,
                          });
                        }}
                        onMouseDown={() => {
                          setSelectedKeg(keg);
                          debugLog(keg);
                        }}
                        onMouseOut={() => {
                          setShowInfo(false);
                        }}
                        onMouseOver={() => {
                          setShowInfo(true);
                        }}
                        position={{
                          lat: +keg?.lastPinnedLocation?.latitude,
                          lng: +keg?.lastPinnedLocation?.longitude,
                        }}
                      >
                        {showInfo && (
                          <div
                            style={{
                              backgroundColor: "red",
                            }}
                          >
                            hdhd
                          </div>
                        )}
                      </Marker>
                    ))
                  }
                </MarkerClusterer>

                {/* client kegs */}
                <MarkerClusterer>
                  {clusterer =>
                    clientKegs.map(keg => (
                      <Marker
                        clusterer={clusterer}
                        icon="/assets/images/blue_MarkerO.png"
                        key={keg?.id}
                        onClick={() => {
                          fetchDirections({
                            lat: +keg?.clientGps?.lat,
                            lng: +keg?.clientGps?.lng,
                          });
                        }}
                        onMouseDown={() => {
                          setSelectedKeg(keg);
                          debugLog(keg);
                        }}
                        onMouseOut={() => {
                          setShowInfo(false);
                        }}
                        onMouseOver={() => {
                          setShowInfo(true);
                        }}
                        position={{
                          lat: +keg?.clientGps?.lat,
                          lng: +keg?.clientGps?.lng,
                        }}
                      >
                        {showInfo && (
                          <div
                            style={{
                              backgroundColor: "red",
                            }}
                          >
                            hdhd
                          </div>
                        )}
                      </Marker>
                    ))
                  }
                </MarkerClusterer>

                {/* partner kegs */}
                <MarkerClusterer>
                  {clusterer =>
                    kegsWithPartner?.map(keg => (
                      <Marker
                        clusterer={clusterer}
                        icon="/assets/images/orange_MarkerO.png"
                        key={keg?.id}
                        onClick={() => {
                          fetchDirections({
                            lat: +keg?.partnerGps?.lat,
                            lng: +keg?.partnerGps?.lng,
                          });
                        }}
                        onMouseDown={() => {
                          setSelectedKeg(keg);
                          debugLog(keg);
                        }}
                        onMouseOut={() => {
                          setShowInfo(false);
                        }}
                        onMouseOver={() => {
                          setShowInfo(true);
                        }}
                        position={{
                          lat: +keg?.partnerGps?.lat,
                          lng: +keg?.partnerGps?.lng,
                        }}
                      >
                        {showInfo && (
                          <div
                            style={{
                              backgroundColor: "red",
                            }}
                          >
                            hdhd
                          </div>
                        )}
                      </Marker>
                    ))
                  }
                </MarkerClusterer>
              </GoogleMap>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MapPage;
